import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Loader2,
  FileText,
  Send,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  Filter,
  Users,
  Package,
} from 'lucide-react';
import { quoteRequestsApi } from '../lib/api';

interface QuoteRequest {
  id: string;
  code: string;
  title: string;
  description?: string;
  projectName?: string;
  status: string;
  sentAt?: string;
  closedAt?: string;
  createdAt: string;
  creator: {
    id: string;
    name: string;
    email: string;
  };
  _count: {
    items: number;
    suppliers: number;
    quotes: number;
  };
}

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  DRAFT: { label: 'Borrador', color: 'bg-slate-500/10 text-slate-400', icon: FileText },
  SENT: { label: 'Enviada', color: 'bg-blue-500/10 text-blue-400', icon: Send },
  PARTIALLY_QUOTED: { label: 'Parcial', color: 'bg-yellow-500/10 text-yellow-400', icon: Clock },
  FULLY_QUOTED: { label: 'Completa', color: 'bg-green-500/10 text-green-400', icon: CheckCircle },
  CLOSED: { label: 'Cerrada', color: 'bg-purple-500/10 text-purple-400', icon: CheckCircle },
  CANCELLED: { label: 'Cancelada', color: 'bg-red-500/10 text-red-400', icon: XCircle },
};

export function Quotations() {
  const navigate = useNavigate();
  const [quoteRequests, setQuoteRequests] = useState<QuoteRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    fetchQuoteRequests();
  }, [statusFilter]);

  const fetchQuoteRequests = async () => {
    try {
      setIsLoading(true);
      const params: any = {};
      if (statusFilter) params.status = statusFilter;

      const response = await quoteRequestsApi.list(params);
      setQuoteRequests(response.data.data?.quoteRequests || []);
    } catch (error) {
      console.error('Error fetching quote requests:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredQuoteRequests = quoteRequests.filter(
    (qr) =>
      qr.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      qr.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (qr.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Cotizaciones</h1>
          <p className="text-slate-400 mt-1">Gestiona tus solicitudes de cotizacion</p>
        </div>
        <button
          onClick={() => navigate('/quotations/new')}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nueva Cotizacion
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por codigo, titulo o proyecto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-800 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-10 pr-8 py-2 bg-slate-900 border border-slate-800 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 appearance-none cursor-pointer"
          >
            <option value="">Todos los estados</option>
            <option value="DRAFT">Borrador</option>
            <option value="SENT">Enviada</option>
            <option value="PARTIALLY_QUOTED">Parcialmente cotizada</option>
            <option value="FULLY_QUOTED">Completamente cotizada</option>
            <option value="CLOSED">Cerrada</option>
            <option value="CANCELLED">Cancelada</option>
          </select>
        </div>
      </div>

      {/* List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="p-8 text-center bg-slate-900 rounded-xl border border-slate-800">
            <Loader2 className="w-6 h-6 text-emerald-400 animate-spin mx-auto" />
          </div>
        ) : filteredQuoteRequests.length === 0 ? (
          <div className="p-8 text-center bg-slate-900 rounded-xl border border-slate-800">
            <FileText className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">
              {searchTerm || statusFilter
                ? 'No se encontraron cotizaciones'
                : 'No hay cotizaciones creadas'}
            </p>
            {!searchTerm && !statusFilter && (
              <button
                onClick={() => navigate('/quotations/new')}
                className="mt-4 text-emerald-400 hover:text-emerald-300"
              >
                Crear tu primera cotizacion
              </button>
            )}
          </div>
        ) : (
          filteredQuoteRequests.map((qr) => {
            const status = statusConfig[qr.status] || statusConfig.DRAFT;
            const StatusIcon = status.icon;

            return (
              <div
                key={qr.id}
                onClick={() => navigate(`/quotations/${qr.id}`)}
                className="bg-slate-900 rounded-xl border border-slate-800 p-4 hover:border-slate-700 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-emerald-400 font-mono text-sm">{qr.code}</span>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${status.color}`}
                      >
                        <StatusIcon className="w-3 h-3" />
                        {status.label}
                      </span>
                    </div>
                    <h3 className="text-lg font-medium text-white truncate">{qr.title}</h3>
                    {qr.projectName && (
                      <p className="text-sm text-slate-400 mt-1">Proyecto: {qr.projectName}</p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Package className="w-4 h-4" />
                        {qr._count.items} items
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {qr._count.suppliers} proveedores
                      </span>
                      {qr._count.quotes > 0 && (
                        <span className="flex items-center gap-1 text-emerald-400">
                          <CheckCircle className="w-4 h-4" />
                          {qr._count.quotes} respuestas
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-sm text-slate-500">{formatDate(qr.createdAt)}</span>
                    <ChevronRight className="w-5 h-5 text-slate-600" />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
